export type OpenAiRoleplayAudioGateCallbacks = {
  /**
   * Released, pre-gain PCM crossed a sustained onset above the idle noise floor
   * (fires even when the actor is muted). Emitted at most once per generation.
   * `observedAt` is an estimated local output onset in the performance.now()
   * domain, never a physical first phoneme.
   */
  onPlaybackStart: (generation: number, observedAt: number) => void;
  /**
   * Metrics-only twin of onPlaybackStart, delivered immediately before it with
   * the same estimated onset. Consumers that only record timing subscribe here.
   */
  onOutputOnset?: (generation: number, observedAt: number) => void;
  /**
   * Explicit provider completion plus real local queue exhaustion, never a quiet
   * gap. `observedAt` estimates when the last rendered sample left the queue.
   * Only follows onPlaybackStart for the same generation.
   */
  onPlaybackDrain: (generation: number, observedAt: number) => void;
  /** Bounded failure for the active generation; `error.reason` names the cause. */
  onFailure: (generation: number, error: OpenAiRoleplayAudioGateError) => void;
};

/**
 * Failure reasons the gate reports. Worklet reasons: `no_input_audio` (no PCM at
 * all by provider stop + grace), `no_output_content` (nonzero PCM but no sustained
 * onset: transport/comfort noise only), `buffer_overflow` (30 s ring exceeded),
 * `approval_timeout` (held for 45 s). Main-thread reasons: `processor_error`,
 * `unavailable` (connect/setup failed).
 */
export type OpenAiRoleplayAudioGateFailureReason =
  | 'no_input_audio'
  | 'no_output_content'
  | 'buffer_overflow'
  | 'approval_timeout'
  | 'processor_error'
  | 'unavailable';

export class OpenAiRoleplayAudioGateError extends Error {
  readonly reason: OpenAiRoleplayAudioGateFailureReason;

  constructor(reason: OpenAiRoleplayAudioGateFailureReason, message?: string) {
    super(message ?? `Audio buffer failed: ${reason}`);
    this.name = 'OpenAiRoleplayAudioGateError';
    this.reason = reason;
  }
}

const WORKLET_REASONS: readonly OpenAiRoleplayAudioGateFailureReason[] = [
  'no_input_audio', 'no_output_content', 'buffer_overflow', 'approval_timeout',
];

const workletReason = (value: unknown): OpenAiRoleplayAudioGateFailureReason => (
  WORKLET_REASONS.find((reason) => reason === value) ?? 'processor_error'
);

type GateCommand = { type: string; generation?: number; held?: boolean; responseId?: string };

type GateMessage = { type?: unknown; generation?: unknown; reason?: unknown; audioTime?: unknown };

/** The remote track has exactly one audible sink: this approval-gated PCM queue. */
export class OpenAiRoleplayAudioGate {
  private context: AudioContext | null = null;
  private node: AudioWorkletNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private gain: GainNode | null = null;
  private decoderElement: HTMLAudioElement | null = null;
  private generation: number | null = null;
  private latestGeneration: number | null = null;
  private responseId: string | null = null;
  private playbackStarted = false;
  private playbackDrained = false;
  private pending: GateCommand[] = [];
  private connecting: Promise<void> | null = null;
  private closed = false;
  private muted = false;

  constructor(private readonly callbacks: OpenAiRoleplayAudioGateCallbacks) {}

  connect(stream: MediaStream): Promise<void> {
    if (this.closed) return Promise.reject(new OpenAiRoleplayAudioGateError('unavailable', 'Audio gate is closed.'));
    if (this.connecting) return this.connecting;
    this.connecting = this.initialize(stream);
    return this.connecting;
  }

  private async initialize(stream: MediaStream) {
    try {
      const context = new AudioContext();
      this.context = context;
      await context.audioWorklet.addModule(`${process.env.PUBLIC_URL || ''}/roleplay-audio-gate.js`);
      if (this.closed) return;
      const node = new AudioWorkletNode(context, 'roleplay-audio-gate', {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [1],
        channelCount: 1,
        channelCountMode: 'explicit',
      });
      this.node = node;
      this.gain = context.createGain();
      // Mute is a downstream gain: the worklet observes pre-gain PCM, so a muted
      // actor still reports onset and drain for captions, persistence and handoff.
      this.gain.gain.value = this.muted ? 0 : 1;
      node.port.onmessage = ({ data }) => this.receive(data as GateMessage);
      node.onprocessorerror = () => this.fail(new OpenAiRoleplayAudioGateError('processor_error', 'Audio buffer processor failed.'));
      this.source = context.createMediaStreamSource(stream);
      this.source.connect(node);
      node.connect(this.gain);
      this.gain.connect(context.destination);
      this.pending.forEach((command) => node.port.postMessage(command));
      this.pending = [];
      await context.resume();
      if (this.closed) return;
      // Chromium's remote WebRTC renderer may not pull decoded samples for a
      // Web Audio source until the stream also has a playing media element.
      // This sink is permanently silent; approval/mute only affect the PCM gate.
      const decoderElement = document.createElement('audio');
      decoderElement.muted = true;
      decoderElement.defaultMuted = true;
      decoderElement.volume = 0;
      decoderElement.setAttribute('playsinline', '');
      decoderElement.srcObject = stream;
      this.decoderElement = decoderElement;
      await decoderElement.play();
    } catch (error) {
      const failure = error instanceof OpenAiRoleplayAudioGateError
        ? error
        : new OpenAiRoleplayAudioGateError('unavailable', error instanceof Error && error.message ? error.message : 'Audio buffer unavailable.');
      this.fail(failure);
      throw failure;
    }
  }

  private receive(data: GateMessage) {
    if (this.closed || this.generation === null || data.generation !== this.generation) return;
    const generation = this.generation;
    if (data.type === 'failure') {
      this.fail(new OpenAiRoleplayAudioGateError(workletReason(data.reason)));
      return;
    }
    // The worklet already fences by generation; a bound response id is not
    // required so a lost `response.created` cannot strand real output.
    if (data.type === 'start' && !this.playbackStarted && !this.playbackDrained) {
      const observedAt = this.observedAt(data.audioTime);
      this.playbackStarted = true;
      this.callbacks.onOutputOnset?.(generation, observedAt);
      if (this.closed || this.generation !== generation) return;
      this.callbacks.onPlaybackStart(generation, observedAt);
      return;
    }
    if (data.type === 'drain' && this.playbackStarted && !this.playbackDrained) {
      const observedAt = this.observedAt(data.audioTime);
      this.playbackDrained = true;
      this.callbacks.onPlaybackDrain(generation, observedAt);
    }
  }

  /**
   * Convert a worklet audio-clock time (AudioContext seconds) into the
   * performance.now() domain. Prefers the context's output timestamp pair, which
   * maps rendering time to device output; otherwise offsets from the current
   * rendering position plus reported output latency. This estimates when the
   * sample leaves the local output path, not when a listener hears a phoneme.
   */
  private observedAt(audioTime: unknown): number {
    const now = performance.now();
    const context = this.context;
    if (typeof audioTime !== 'number' || !Number.isFinite(audioTime) || !context) return now;
    const stamp = typeof context.getOutputTimestamp === 'function' ? context.getOutputTimestamp() : undefined;
    if (stamp && typeof stamp.contextTime === 'number' && typeof stamp.performanceTime === 'number'
      && Number.isFinite(stamp.contextTime) && Number.isFinite(stamp.performanceTime) && stamp.performanceTime > 0) {
      return Math.max(0, stamp.performanceTime + (audioTime - stamp.contextTime) * 1000);
    }
    const current = context.currentTime;
    if (typeof current !== 'number' || !Number.isFinite(current)) return now;
    const outputLatency = typeof context.outputLatency === 'number' && Number.isFinite(context.outputLatency)
      ? context.outputLatency : 0;
    return Math.max(0, now + (audioTime - current + outputLatency) * 1000);
  }

  private send(command: GateCommand) {
    if (this.closed) return;
    if (this.node) this.node.port.postMessage(command);
    else this.pending.push(command);
  }

  private fail(error: OpenAiRoleplayAudioGateError) {
    if (this.closed) return;
    const generation = this.generation;
    this.discard();
    if (generation !== null) this.callbacks.onFailure(generation, error);
  }

  /** Begin a generation. Generations are monotonic per gate; replays and stale starts are inert. */
  startTurn(generation: number, options: { held: boolean }) {
    if (this.closed || !Number.isFinite(generation)) return;
    if (this.latestGeneration !== null && generation <= this.latestGeneration) return;
    this.generation = generation;
    this.latestGeneration = generation;
    this.responseId = null;
    this.playbackStarted = false;
    this.playbackDrained = false;
    this.pending = [];
    this.send({ type: 'start', generation, held: options.held });
  }

  /** Correlate the matching speech response (first binding wins). */
  bindResponse(generation: number, responseId: string) {
    if (this.closed || this.generation !== generation || !responseId || this.responseId !== null) return;
    this.responseId = responseId;
    this.send({ type: 'bind', generation, responseId });
  }

  /** Provider `output_audio_buffer.started` for the bound response: correlation only, never authorization. */
  providerStarted(generation: number, responseId: string) {
    if (this.closed || this.generation !== generation || !responseId) return;
    if (this.responseId === null) this.responseId = responseId;
    if (this.responseId !== responseId) return;
    this.send({ type: 'provider-start', generation, responseId });
  }

  release(generation: number) {
    if (this.generation === generation) this.send({ type: 'release', generation });
  }

  finishTurn(generation: number) {
    if (this.generation === generation) this.send({ type: 'finish', generation });
  }

  discard(generation?: number) {
    if (generation !== undefined && this.generation !== generation) return;
    const active = this.generation;
    this.generation = null;
    this.responseId = null;
    this.playbackStarted = false;
    this.playbackDrained = false;
    this.pending = [];
    if (active !== null) this.send({ type: 'discard', generation: active });
  }

  clearPlayback() { this.discard(); }

  setMuted(muted: boolean) {
    this.muted = muted;
    if (this.gain) this.gain.gain.value = muted ? 0 : 1;
  }

  close() {
    if (this.closed) return;
    this.discard();
    this.send({ type: 'close' });
    this.closed = true;
    this.pending = [];
    this.source?.disconnect();
    this.node?.disconnect();
    this.gain?.disconnect();
    if (this.decoderElement) {
      this.decoderElement.pause();
      this.decoderElement.srcObject = null;
      this.decoderElement = null;
    }
    if (this.node) {
      this.node.port.onmessage = null;
      this.node.onprocessorerror = null;
      this.node.port.close();
    }
    if (this.context && this.context.state !== 'closed') void this.context.close().catch(() => undefined);
    this.source = null;
    this.node = null;
    this.gain = null;
    this.context = null;
  }
}
